import { useMemo } from 'react';
import { Copy, Download, ImageMinus, RotateCcwIcon, SquareCheck, Trash2 } from 'lucide-react';
import { useDuplicateAssetAction } from '@/hooks/data/assets/useDuplicateAssetAction';
import { SelectionAction } from '@/components/toolbars/selection-action';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useSelection } from '@/hooks/data/selection/useSelection';
import { DAMNodeType, NodeType, toNodeType } from '@/types/tree';
import { shareHasDownloadableContent } from '@/utilities/share';
import { type Asset, isAssetLoading } from '@/types/asset';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useTreeContext } from '@/context/TreeContext';
import { Button } from '@/components/ui/button';
import { cn, isAsset } from '@/lib/utils';
import { useTranslations } from 'next-intl';

/**
 * Separate component so useDuplicateAssetAction()'s mutation hooks only mount
 * once this action is shown, not on every SelectionBar render.
 */
const DuplicateSelectionAction = ({
  selectedAssets,
  destinationFolderId,
  disabled,
  isAllSelected,
  rowCount,
  includeSubFolderAssets,
}: {
  selectedAssets: Array<Asset>;
  destinationFolderId: string;
  disabled?: boolean;
  isAllSelected: boolean;
  rowCount: number;
  includeSubFolderAssets: boolean;
}) => {
  const tCommon = useTranslations('common');
  const { duplicateAsset, confirmDuplicateAssets, confirmDuplicateAllAssets } = useDuplicateAssetAction();

  return (
    <SelectionAction
      icon={Copy}
      tooltip={tCommon('duplicate')}
      dataCy="selection-bar-duplicate-button"
      disabled={disabled}
      onClick={() => {
        if (isAllSelected) {
          confirmDuplicateAllAssets(rowCount, destinationFolderId, includeSubFolderAssets);
          return;
        }
        if (selectedAssets.length > 1) {
          confirmDuplicateAssets(selectedAssets, destinationFolderId);
          return;
        }
        const [only] = selectedAssets;
        if (only) {
          void duplicateAsset(only.id, destinationFolderId, only.name);
        }
      }}
    />
  );
};

export const SelectionBar = ({
  rowCount,
  nodeName,
  selectedAssets,
  setSelectedAssets,
  isUpload,
  shareDownload,
}: {
  rowCount: number;
  nodeName: string;
  selectedAssets: Array<Asset>;
  setSelectedAssets: (ids: Array<Asset>) => void;
  isUpload?: boolean;
  // Present on the public share gallery when the share allows downloads. Drives a
  // share-scoped download (always share-scoped, even for authenticated tenant
  // members) instead of the capability-gated authenticated DownloadZip.
  shareDownload?: {
    shareId: string;
    downloadableVariants: Array<{ id: string; name: string; slug: string }>;
    name?: string;
    includeOriginalFile?: boolean;
  };
}) => {
  const tSelection = useTranslations('selection');
  const tMenuNode = useTranslations('menu.node');

  const { isBrowsePage, isTrashPage, isDuplicatesPage, isSharesPage, isPublicSharePage } = useCurrentPage();
  const search = useIsSearching();
  const {
    isAllSelected: browserIsAllSelected,
    setIsAllSelected,
    selectedNode,
    selectedChild,
    operationTarget: target,
  } = useSelection();
  const { setCurrentSelectedType, includeSubFolderAssets } = useTreeContext();
  const { getActiveUploadSession, removeAssets, setUploadField } = useUploadStore();
  const { uploadSessionId, session } = getActiveUploadSession();
  const { canDownloadAssets, canSelectAll, canViewDeleteOrRemoveAction } = useCapabilities();

  // A selected child folder/album (DM-2517). `selectedAssets` is empty in that case, and
  // `[].every(...)` is vacuously true — so every trashed-state check below has to be decided
  // explicitly for a node rather than inheriting that `true`, which would otherwise hide
  // download and trash and wrongly offer restore.
  const isChildSelected = selectedChild !== undefined;

  const onlyTrashedAssetsSelected = useMemo(
    () => !isChildSelected && selectedAssets.every((selection) => selection.trashed),
    [isChildSelected, selectedAssets],
  );

  const someTrashedAssetsSelected = useMemo(
    () => !isChildSelected && selectedAssets.some((selection) => selection.trashed),
    [isChildSelected, selectedAssets],
  );

  const openModal = useOpenModal();
  const isAllSelected = useMemo(
    () => (isUpload ? session.isAllSelected : browserIsAllSelected),
    [browserIsAllSelected, isUpload, session.isAllSelected],
  );
  const clearSelection = () => {
    // No active node means nothing is being browsed, which the panel reads as folders.
    const nodeType = selectedNode ? toNodeType(selectedNode.type) : NodeType.Folders;

    if (isUpload) {
      setUploadField(uploadSessionId, 'isAllSelected', false);
      setUploadField(uploadSessionId, 'currentSelectedType', nodeType);
    } else {
      setIsAllSelected(false);
      setCurrentSelectedType(nodeType);
    }
    setSelectedAssets([]);
  };

  // Describes the selected thing, not the active node: a selected child reports its own type
  // so the count and every tooltip read "1 folder"/"1 album" rather than an asset count.
  const selectionType = isUpload
    ? DAMNodeType.Asset
    : isChildSelected
      ? selectedChild.type
      : selectedAssets.every((item) => isAsset(item.type))
        ? DAMNodeType.Asset
        : selectedAssets.every((item) => item.type === DAMNodeType.Album)
          ? DAMNodeType.Album
          : selectedAssets.every((item) => item.type === DAMNodeType.Folder)
            ? DAMNodeType.Folder
            : 'item';

  const selectedCount = isChildSelected ? 1 : isAllSelected ? rowCount : selectedAssets.length;

  // Verb and icon follow the *selected* thing. "Remove" only means anything for an asset sitting
  // inside an album, so a selected child reads as trash — which is what it does, through the same
  // node mutation and the same Trash2 glyph as the tree's own DeleteNodeMenuItem.
  const isRemoveAction = isUpload || (!isChildSelected && selectedNode?.type === DAMNodeType.Album);
  const shouldShowDownloadZipAction =
    !isUpload && !isPublicSharePage && canDownloadAssets && !isTrashPage && !onlyTrashedAssetsSelected;
  const shouldShowRestoreAction = (isTrashPage || onlyTrashedAssetsSelected) && !isUpload;
  const shouldShowDeleteOrRemoveAction = (isUpload || canViewDeleteOrRemoveAction) && !onlyTrashedAssetsSelected;
  // On the share gallery the download is always share-scoped (even for tenant
  // members), so it takes precedence over the authenticated DownloadZip action —
  // but only once canDownloadAssets confirms the viewer isn't anonymous.
  const shouldShowDownloadAction = (Boolean(shareDownload) && canDownloadAssets) || shouldShowDownloadZipAction;
  /**
   * Positive check, not "!== Album": selectedNode is undefined outside a folder
   * context (e.g. Home), where a negative check would let the button render
   * with nowhere to send the duplicate and silently no-op on click.
   */
  const duplicateDestinationFolderId = selectedNode?.type === DAMNodeType.Folder ? selectedNode.node.id : undefined;
  const shouldShowDuplicateAction =
    !isUpload &&
    !isTrashPage &&
    !isDuplicatesPage &&
    !isSharesPage &&
    !search &&
    !someTrashedAssetsSelected &&
    selectedAssets.length > 0 &&
    duplicateDestinationFolderId !== undefined;

  const deleteOrRemoveSelection = () => {
    if (isUpload) {
      removeAssets(uploadSessionId);
      return;
    }

    // A selected child trashes through exactly the payload DeleteNodeMenuItem sends from the
    // tree's context menu — same modal, same mutation, same confirmation. Not a second path.
    if (isChildSelected) {
      openModal(ModalId.DeleteConfirmation, {
        id: selectedChild.id,
        name: selectedChild.name,
        data: { type: selectedChild.type },
      });
      return;
    }

    openModal(ModalId.DeleteConfirmation, {
      count: rowCount,
      ...(selectionType ? { data: { type: selectionType } } : {}),
    });
  };

  return (
    <div className="flex h-12 max-h-12 min-h-12 items-center justify-between gap-x-2 bg-highlight-base p-2">
      <div className="flex items-center gap-x-2">
        <SelectionAction
          icon={SquareCheck}
          tooltip={tSelection('clearSelection')}
          dataCy="deselect-checkbox-button"
          onClick={clearSelection}
        />

        <div className="text-xs whitespace-nowrap text-text-inverted">
          {tSelection('selectedCount', { count: selectedCount, type: selectionType })}
        </div>
        {isBrowsePage && !search && canSelectAll && !isChildSelected && (
          <Button
            variant="ghost"
            className={cn(
              'min-h-auto p-0! px-2 text-xs font-bold text-text-inverted! hover:bg-transparent hover:underline focus:outline-hidden focus-visible:ring-0 dark:hover:bg-transparent',
              {
                'line-clamp-1! break-all': !isAllSelected,
              },
            )}
            data-cy="select-all-toolbar-button"
            onClick={() => {
              if (isAllSelected) {
                clearSelection();
              } else {
                // Select-all is assets-only and must never leave a child in the selection —
                // writing assets flips the union off the nodes arm (DM-2517).
                setSelectedAssets([]);
                if (isUpload) {
                  setUploadField(uploadSessionId, 'isAllSelected', true);
                } else {
                  setIsAllSelected(true);
                }
                setCurrentSelectedType(NodeType.Assets);
              }
            }}
          >
            {isAllSelected
              ? tSelection('clearSelection')
              : tSelection('selectAllAssetsInNode', { count: rowCount, nodeName })}
          </Button>
        )}
      </div>
      <div className="flex gap-x-2">
        {shouldShowDownloadAction && (
          <SelectionAction
            icon={Download}
            tooltip={
              !shareDownload && someTrashedAssetsSelected
                ? tSelection('trashedAssetsTooltip')
                : tMenuNode('downloadZip')
            }
            dataCy={shareDownload ? 'selection-bar-share-download-button' : 'selection-bar-download-zip-button'}
            disabled={
              (selectedAssets.length === 0 && !isChildSelected) ||
              (shareDownload ? !shareHasDownloadableContent(shareDownload) : someTrashedAssetsSelected)
            }
            onClick={() =>
              shareDownload
                ? openModal(ModalId.ShareDownload, {
                    shareId: shareDownload.shareId,
                    nodeIds: selectedAssets.map((asset) => asset.id),
                    downloadableVariants: shareDownload.downloadableVariants,
                    name: shareDownload.name,
                    assets: selectedAssets,
                    includeOriginalFile: shareDownload.includeOriginalFile,
                  })
                : openModal(ModalId.DownloadZip, {
                    nodeIds: target.nodeIds,
                    sourceType: target.sourceType,
                    name: target.name,
                    assets: target.assets,
                  })
            }
          />
        )}
        {shouldShowDuplicateAction && duplicateDestinationFolderId && (
          <DuplicateSelectionAction
            selectedAssets={selectedAssets}
            destinationFolderId={duplicateDestinationFolderId}
            disabled={selectedAssets.some((selection) => isAssetLoading(selection))}
            isAllSelected={isAllSelected}
            rowCount={rowCount}
            includeSubFolderAssets={includeSubFolderAssets}
          />
        )}
        {shouldShowRestoreAction && (
          <SelectionAction
            icon={RotateCcwIcon}
            tooltip={tSelection('restoreTooltip', { count: selectedAssets.length, type: selectionType })}
            dataCy="selection-bar-restore-trash"
            onClick={() => openModal(ModalId.RestoreTrashConfirmation)}
          />
        )}
        {shouldShowDeleteOrRemoveAction && (
          <SelectionAction
            icon={isRemoveAction ? ImageMinus : Trash2}
            tooltip={
              someTrashedAssetsSelected
                ? tSelection('trashedAssetsTooltip')
                : tSelection('actionTooltip', {
                    action: tSelection(isRemoveAction ? 'removeAction' : 'trashAction'),
                    count: selectedCount,
                    type: selectionType,
                  })
            }
            dataCy="selection-bar-delete-remove-asset-button"
            onClick={deleteOrRemoveSelection}
            disabled={someTrashedAssetsSelected}
          />
        )}
      </div>
    </div>
  );
};
